import classNames from 'classnames';
import styles from './Article.module.scss';
import { UI_HeroCard, UI_HeroCardProps } from 'ui/components/2-molecules/Card/HeroCard/HeroCard';
import { UI_TopLevelGrid } from '../TopLevelGrid/TopLevelGrid';
import { UI_TopLevelGridCell } from '../TopLevelGrid/TopLevelGridCell';
import { UI_ArticleAuthor, UI_ArticleAuthorProps } from 'ui/components/2-molecules/Article/ArticleAuthor/ArticleAuthor';
import { UI_ArticleInfo, UI_ArticleInfoProps } from 'ui/components/2-molecules/Article/ArticleInfo/ArticleInfo';
import { UI_RichText } from 'ui/components/1-atoms/RichText/RichText';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { UI_Paragraph } from 'ui/components/1-atoms/Paragraph/Paragraph';
import { UI_Container } from '../Container/Container';
import { UI_ArticleDate } from 'ui/components/2-molecules/Article/ArticleDate/ArticleDate';
import { UI_ArticleMedia, UI_ArticleMediaProps } from 'ui/components/2-molecules/Article/ArticleMedia/ArticleMedia';
import { UI_ArticleTextProps } from 'ui/components/2-molecules/Article/ArticleText/ArticleText';
import { LightBgColor } from 'helpers/types';
import { useState } from 'react';
import HubSpotForm from 'ui/components/2-molecules/HubSpotForm/HubSpotForm';

export interface UI_ArticleProps {
	className?: string;
	articleAuthors?: UI_ArticleAuthorProps[];
	showAuthorsBio?: boolean;
	showArticleAuthorsBio?: boolean;
	articleAuthorHeading?: string;
	articleInfo?: UI_ArticleInfoProps;
	articleTeaser?: string;
	articleHeading?: string;
	articleTeaserHeading?: string;
	articleDate?: string;
	articleDateStatus?: string;
	richTextSections?: UI_ArticleTextProps[] | UI_ArticleMediaProps[];
	articleShowFormButtonText?: string;
	layoutType?: 'Article' | 'Event';
	articleBgColor?: LightBgColor;
	formHtmlCode?: string;
	eventDisplayButtonRight?: boolean;
	placeholderLabel?: string;
	globalTheme?: string;
	authorSidebarBottom?: React.ReactNode;
	buttonStyle?: 'primary' | 'secondary';
	heroCard?: UI_HeroCardProps;
}

export const UI_Article: React.FC<UI_ArticleProps> = ({
	className,
	articleAuthors,
	showArticleAuthorsBio,
	showAuthorsBio,
	articleAuthorHeading,
	articleInfo,
	articleTeaser,
	articleTeaserHeading,
	articleHeading,
	richTextSections,
	articleDate,
	layoutType = 'Article',
	articleBgColor,
	articleShowFormButtonText,
	formHtmlCode,
	articleDateStatus,
	eventDisplayButtonRight,
	placeholderLabel,
	globalTheme,
	authorSidebarBottom,
	buttonStyle,
	heroCard,
}) => {
	const [showForm, setShowForm] = useState(false);
	const articleBgUtilityClass = classNames({
		'u-color___whiteGrey': articleBgColor === LightBgColor.Grey,
		'u-color___lilac': articleBgColor === LightBgColor.Lilac,
		'u-color___turqoise': articleBgColor === LightBgColor.Turqoise,
		'u-color___pink': articleBgColor === LightBgColor.Pink,
		'u-color___sand': articleBgColor === LightBgColor.Sand,
		'u-color___lightBlue': articleBgColor === LightBgColor.LightBlue,
	});
	const onButtonClick = () => {
		setShowForm(true);
	};

	return layoutType === 'Article' ? (
		<>
			<div className={classNames(styles.Article, className)}>
				{heroCard?.heading && <UI_HeroCard className={styles.Article_heroCard} {...heroCard} />}
				<UI_Container width="Standard">
					<UI_TopLevelGrid wrap className={styles.Article_boxInfo}>
						<UI_TopLevelGridCell
							desktopWidth={globalTheme === 'kogp' ? '6col' : '8col'}
							mobileWidth="100"
							className={styles.Article_authorsContainer}
						>
							{articleAuthorHeading && (
								<UI_Paragraph bold className={styles.Article_articleAuthorHeading}>
									{articleAuthorHeading}
								</UI_Paragraph>
							)}
							{articleAuthors &&
								articleAuthors.map((articleAuthor, index) => (
									<UI_ArticleAuthor
										key={index}
										className={styles.Article_articleAuthor}
										{...articleAuthor}
										showAuthorsBio={showAuthorsBio}
									/>
								))}
							{articleDate && (
								<UI_ArticleDate date={articleDate} dateStatus={articleDateStatus} className={styles.Article_articleDate} />
							)}
							{authorSidebarBottom}
						</UI_TopLevelGridCell>

						<UI_TopLevelGridCell
							desktopWidth={globalTheme === 'kogp' ? '16col' : '12col'}
							mobileWidth="100"
							className={styles.Article_content}
						>
							{articleInfo && <UI_ArticleInfo className={styles.Article_articleInfo} {...articleInfo} />}
							{articleTeaser && <p className={styles.Article_articleTeaser}>{articleTeaser}</p>}
							{articleHeading && (
								<UI_Heading headingLevel="h2" style="md" className={styles.Article_articleHeading___article}>
									{articleHeading}
								</UI_Heading>
							)}
							{richTextSections &&
								richTextSections.map((section, index) => {
									if (section && section.documentType === 'articleMedia') {
										return (
											<div className={styles.Article_mediaSectionContainer} key={index}>
												<div className={styles.Article_imageSection}>
													<UI_ArticleMedia key={index} className={styles.Article_image} {...section} />
												</div>
											</div>
										);
									} else {
										return (
											<div className={styles.Article_richTextSectionContainer} key={index}>
												<UI_RichText key={index} className={styles.Article_richTextSection} content={section.content} />
											</div>
										);
									}
								})}
						</UI_TopLevelGridCell>
					</UI_TopLevelGrid>
				</UI_Container>
			</div>
		</>
	) : (
		<>
			<div className={classNames(styles.Article, className)}>
				{heroCard?.heading && <UI_HeroCard {...heroCard} />}

				<div>
					<UI_Container width="Standard" fillWidth>
						<UI_TopLevelGrid
							wrap
							className={classNames(styles.Article_boxInfo, articleBgUtilityClass, {
								[styles['Article--articleBgColorGrey']]: articleBgColor === LightBgColor.Grey,
								[styles['Article--articleBgColorLilac']]: articleBgColor === LightBgColor.Lilac,
								[styles['Article--articleBgColorTurqoise']]: articleBgColor === LightBgColor.Turqoise,
								[styles['Article--articleBgColorPink']]: articleBgColor === LightBgColor.Pink,
								[styles['Article--articleBgColorSand']]: articleBgColor === LightBgColor.Sand,
								[styles['Article--articleBgColorLightBlue']]: articleBgColor === LightBgColor.LightBlue,
							})}
						>
							<UI_TopLevelGridCell
								desktopWidth={globalTheme === 'kogp' ? '6col' : '8col'}
								mobileWidth="100"
								className={classNames(styles.Article_authorsContainer___event)}
							>
								{articleAuthorHeading && (
									<UI_Paragraph bold className={styles.Article_articleAuthorHeading}>
										{articleAuthorHeading}
									</UI_Paragraph>
								)}
								{articleAuthors &&
									articleAuthors.map((articleAuthor, index) => (
										<UI_ArticleAuthor
											key={index}
											className={styles.Article_articleAuthor}
											showArticleAuthorsBio={showArticleAuthorsBio}
											{...articleAuthor}
										/>
									))}
								{articleDate && (
									<UI_ArticleDate date={articleDate} dateStatus={articleDateStatus} className={styles.Article_articleDate} />
								)}
								{authorSidebarBottom}
							</UI_TopLevelGridCell>

							<UI_TopLevelGridCell
								desktopWidth={globalTheme === 'kogp' ? '12col' : '16col'}
								mobileWidth="100"
								className={classNames(styles.Article_content___event)}
							>
								{articleTeaserHeading && (
									<UI_Paragraph bold className={styles.Article_articleAuthorHeading}>
										{articleTeaserHeading}
									</UI_Paragraph>
								)}
								{articleTeaser && <p className={styles.Article_articleTeaser}>{articleTeaser}</p>}
								{articleInfo && (
									<UI_ArticleInfo
										className={styles.Article_articleInfo}
										{...articleInfo}
										buttonStyle={buttonStyle}
										button={
											articleShowFormButtonText && formHtmlCode
												? { text: articleShowFormButtonText, onClick: onButtonClick, showButton: showForm }
												: undefined
										}
									/>
								)}

								<UI_TopLevelGrid wrap nogutter>
									<UI_TopLevelGridCell desktopWidth={globalTheme === 'kogp' ? '12col' : '16col'} mobileWidth="100">
										<div style={{ display: showForm ? 'block' : 'none' }}>
											<HubSpotForm
												formHtmlCode={formHtmlCode}
												displayButtonRight={eventDisplayButtonRight}
												placeholderLabel={placeholderLabel}
												buttonStyle={buttonStyle}
											/>
										</div>
									</UI_TopLevelGridCell>
								</UI_TopLevelGrid>
							</UI_TopLevelGridCell>
						</UI_TopLevelGrid>
					</UI_Container>
				</div>
				<div>
					<UI_Container width="Standard" fillWidth>
						<UI_TopLevelGrid wrap nogutter>
							<UI_TopLevelGridCell desktopWidth={globalTheme === 'kogp' ? '6col' : '8col'} mobileWidth="100">
								{``}
							</UI_TopLevelGridCell>
							<UI_TopLevelGridCell
								desktopWidth="12col"
								mobileWidth="100"
								className={classNames(styles.Article_content___eventDescription)}
							>
								{articleHeading && (
									<UI_Heading headingLevel="h2" style="md" className={classNames(styles.Article_articleHeading)}>
										{articleHeading}
									</UI_Heading>
								)}
								{richTextSections &&
									richTextSections.map((section, index) => {
										if (section && section.documentType === 'articleMedia') {
											return (
												<div className={styles.Article_mediaSectionContainer} key={index}>
													<div className={styles.Article_imageSection}>
														<UI_ArticleMedia key={index} className={styles.Article_image} {...section} />
													</div>
												</div>
											);
										} else {
											return (
												<div className={styles.Article_richTextSectionContainer} key={index}>
													<UI_RichText
														key={index}
														className={styles.Article_richTextSection}
														content={section.content}
														readMoreButtonText={section.readMoreButtonText}
														readMoreText={section.readMoreText}
													/>
												</div>
											);
										}
									})}
							</UI_TopLevelGridCell>
						</UI_TopLevelGrid>
					</UI_Container>
				</div>
			</div>
		</>
	);
};
